import type { Camera } from '../../core/domain/camera';
import type { StreamFrame } from '../../core/bridge/types';
import type { BackendClient } from '../bridge/backend-client';
import type { DecodeOptions } from '../../core/media/snapshot-decode';

/*
 * CameraManager — main-thread PROXY of the camera ROSTER (the list, not the
 * media). One streaming subscription (a MessagePort) over Video.Subscribe;
 * local cache from snapshot + deltas; re-emits `change`. Identical shape to
 * ControllerManager — components subscribe via addEventListener('change', …).
 *
 * Scope boundary: this owns the camera DESCRIPTORS only (identity, negotiated
 * media facts, producer-feeding status, viewer count). The live MEDIA and its
 * lifecycle — RTCPeerConnection, the shared MediaStream, look-away pause/resume,
 * reconnect — are owned by the main-thread CameraStreamManager. Same split as
 * Controller (descriptor) vs the live value plane.
 */
export class CameraManager extends EventTarget {
  private readonly cache = new Map<string, Camera>();
  private port?: MessagePort;

  constructor(
    private readonly backend: BackendClient,
    private readonly gatewayId: string,
  ) {
    super();
  }

  get all(): Camera[] {
    return [...this.cache.values()];
  }

  get(id: string): Camera | undefined {
    return this.cache.get(id);
  }

  /** The gateway this manager is scoped to — set as X-Vitesse-Gateway-Id on the
   *  recording HLS + export requests so the CLOUD api-gateway pulls recordings
   *  from the right gateway (the Connect transport conveys the same header). */
  get gateway(): string {
    return this.gatewayId;
  }

  async start(): Promise<void> {
    this.cache.clear();
    this.dispatchEvent(new CustomEvent('change'));
    this.port = await this.backend.api.subscribeCameras(this.gatewayId);
    this.port.onmessage = (e: MessageEvent<StreamFrame<Camera>>) => this.onFrame(e.data);
    this.port.start();
  }

  stop(): void {
    this.port?.postMessage({ kind: 'close' });
    this.port = undefined;
    this.cache.clear();
  }

  /** Toggle recording for one camera. The video-engine flips
   *  CameraDescriptor.recording_enabled and republishes, so the new state arrives
   *  via this roster subscription (a `change` event) — no optimistic update. */
  async setRecording(cameraId: string, enabled: boolean): Promise<void> {
    await this.backend.api.setCameraRecording(this.gatewayId, cameraId, enabled);
  }

  /** Pause/resume recording (keeps footage + stays enabled). */
  async setRecordingPaused(cameraId: string, paused: boolean): Promise<void> {
    await this.backend.api.setCameraRecordingPaused(this.gatewayId, cameraId, paused);
  }

  /** Overwrite-oldest (rolling) vs stop-at-cap. */
  async setRecordingCircular(cameraId: string, circular: boolean): Promise<void> {
    await this.backend.api.setCameraRecordingCircular(this.gatewayId, cameraId, circular);
  }

  /** The HLS DVR playlist URL for a camera (hls.js loads it; the Bearer rides
   *  xhrSetup via getToken()). */
  async hlsPlaylistUrl(cameraId: string): Promise<string> {
    const base = await this.backend.api.cameraMediaBase(this.gatewayId);
    return `${base}/v1/hls/${cameraId}/playlist.m3u8`;
  }

  /** Server-export URL — the whole recorded span muxed into one downloadable MP4. */
  async exportUrl(cameraId: string): Promise<string> {
    const base = await this.backend.api.cameraMediaBase(this.gatewayId);
    return `${base}/v1/hls/${cameraId}/export.mp4`;
  }

  /** Current session JWT (for hls.js xhrSetup + the authed export download). */
  getToken(): Promise<string | null> {
    return this.backend.getAuthToken();
  }

  /** Snapshot one camera as a decoded image Blob. atNs omitted/0n ⇒ the latest LIVE
   *  frame (what it's showing now); atNs>0 ⇒ the nearest recorded keyframe to that
   *  moment. The edge ships ONE encoded keyframe; the worker decodes it (WebCodecs)
   *  on the viewer's own idle hardware — never loading the compute-strained source.
   *  The AI snapshot tool + the (cloud) poster call this; pass opts.maxDimension for
   *  a tile-sized poster from the same full-res keyframe. */
  snapshot(cameraId: string, atNs: bigint = 0n, opts?: DecodeOptions): Promise<Blob> {
    return this.backend.api.snapshotImage(this.gatewayId, cameraId, atNs, opts);
  }

  private onFrame(frame: StreamFrame<Camera>): void {
    switch (frame.kind) {
      case 'snapshot':
        this.cache.clear();
        for (const c of frame.items) this.cache.set(c.id, c);
        break;
      case 'delta':
        if (frame.op === 'remove') this.cache.delete(frame.item.id);
        else this.cache.set(frame.item.id, frame.item);
        break;
      case 'error':
        // Data plane down — but KEEP the cache so a transient blip doesn't blank the
        // camera list for the reconnect window; the next snapshot replaces it
        // wholesale. See ControllerManager.onFrame for the full rationale.
        this.dispatchEvent(new CustomEvent('change'));
        return;
    }
    this.dispatchEvent(new CustomEvent('change'));
  }
}
